import * as React from 'react';
import { SelectFilter } from '../../../utils/ReactSelectFastFilter/ReactSelectFastFilter';
import styled from 'components/_UI/designSystem';
import { Icon } from 'components/spaceKit';
import selectStyles from './selectStyles';

const SelectVirtual: any = (require('react-virtualized-select') as any).default;
const Select: any = require('react-select') as any;

const StyledVirtualSelect = styled.div`
    ${selectStyles};
`;

interface VirtualSelectProps {
    key?: string;
    className?: string;
    multi?: boolean;
    clearable?: boolean;
    isLoading?: boolean;
    autoBlur?: boolean;
    disabled?: boolean;
    closeOnSelect?: boolean;
    placeholder?: string;
    name?: string;
    value?: any;
    valueKey?: string;
    labelKey?: string;
    searchable?: boolean;
    onChange?: any;
    options?: any[];
    optionRenderer?: any;
    valueRenderer?: any;
    promptTextCreator?: (label: string) => string;
    showNewOptionAtTop?: boolean;
    scrollOnOpen?: boolean;
    filterOptions?: SelectFilter;
}

export default class VirtualSelect extends React.Component<VirtualSelectProps, any> {
    private boundGetOptionHeight = this.getOptionHeight.bind(this);
    private parentWidth = 0;
    private fontFamily: string;
    private fontSize: string;
    private textDiv: HTMLElement;
    private cache: { [key: string]: number } = {};
    private optionRenderer: any;
    private target;

    constructor(props) {
        super(props);
        this.target = React.createRef();
        if (this.props.optionRenderer) {
            this.optionRenderer = this.renderOption.bind(this, this.props.optionRenderer);
        }
    }

    componentWillUnmount() {
        if (this.textDiv) {
            document.body.removeChild(this.textDiv);
            this.textDiv = null;
        }
    }

    render() {
        let props: any;

        if (this.optionRenderer) {
            props = { ...this.props };
            props.optionRenderer = this.optionRenderer;
        } else {
            props = this.props;
        }

        let component: any;
        if (props.showNewOptionAtTop) {
            component = Select.Creatable;
        }

        return (
            <StyledVirtualSelect className={'Select'} ref={this.target}>
                <SelectVirtual
                    optionHeight={this.boundGetOptionHeight}
                    removeSelected={false}
                    arrowRenderer={this.arrowRenderer}
                    selectComponent={component}
                    onOpen={this.onOpen}
                    {...props}
                />
            </StyledVirtualSelect>
        );
    }

    arrowRenderer = () => (
        <Icon title='Clear Override' value='chevron-down' size='s' onClick={undefined} />
    );

    private getParentDetails() {
        if (!this.target.current) {
            return;
        }

        const rect = this.target.current.getBoundingClientRect();
        this.parentWidth = rect.width;

        const computedStyle = window.getComputedStyle(this.target.current, null);

        this.fontFamily = computedStyle.getPropertyValue('font-family');
        this.fontSize = computedStyle.getPropertyValue('font-size');
    }

    private getOptionHeight(item: any) {
        if (!this.parentWidth) {
            this.getParentDetails();
        }
        return this.getTextHeight(item.option[this.props.labelKey]);
    }

    private onOpen = () => {
        if (!this.target.current || !this.props.scrollOnOpen) {
            return;
        }
        this.target.current.scrollIntoView();
    };

    // https://stackoverflow.com/questions/118241/calculate-text-width-with-javascript/21015393#21015393
    private getTextHeight(text: string): number {
        // Create a key containing each word length, concatenated together like: "35543"
        const key = text.split(' ').reduce((result: string, value: string) => {
            return (result += value.length);
        }, '');

        // Look first to see if the height is cached
        let height = this.cache[key];

        if (!height) {
            if (!this.textDiv) {
                this.textDiv = document.createElement('div');

                document.body.appendChild(this.textDiv);
                this.textDiv.style.fontFamily = this.fontFamily;
                this.textDiv.style.position = 'absolute';
                this.textDiv.style.top = '-100%';
                this.textDiv.style.visibility = 'hidden';
                this.textDiv.style.width = this.parentWidth - 1 + 'px';
                this.textDiv.style.minHeight = '40px';
                this.textDiv.style.lineHeight = '18px';
                this.textDiv.style.fontSize = '13px';
                this.textDiv.style.padding = '8px 10px';
            }

            this.textDiv.innerHTML = text;

            height = this.cache[key] = Math.round(this.textDiv.clientHeight);
        }

        return height;
    }

    private renderOption(
        render: any,
        arg: {
            option: any;
            focusedOption: any;
            style: any;
            valueArray: any[];
            focusOption: any;
            selectValue: any;
        }
    ) {
        const option = arg.option;
        const className = ['VirtualizedSelectOption'];

        if (option === arg.focusedOption) {
            className.push('VirtualizedSelectFocusedOption');
        }

        if (option.disabled) {
            className.push('VirtualizedSelectDisabledOption');
        }

        if (arg.valueArray && arg.valueArray.indexOf(option) >= 0) {
            className.push('VirtualizedSelectSelectedOption');
        }

        if (option.className) {
            className.push(option.className);
        }

        const events = option.disabled
            ? {}
            : {
                  onClick: () => arg.selectValue(option),
                  onMouseEnter: () => arg.focusOption(option),
              };

        return (
            <div key={option.id} style={arg.style} className={className.join(' ')} {...events}>
                {render(option)}
            </div>
        );
    }
}
